/**
 * @fileoverview Error Tracking Service
 *
 * Provider-agnostic error tracking. The rest of the app imports from here.
 * To switch providers, change the tracker instantiation below.
 *
 * @module services/errorTracking
 */

import { NoopTracker } from './NoopTracker';
import { getErrorTrackerConfig } from './config';
import type { ErrorTracker, ErrorTrackerUser, ErrorTrackerBreadcrumb } from './types';

export type { ErrorTracker, ErrorTrackerUser, ErrorTrackerBreadcrumb } from './types';

const noopTracker: ErrorTracker = new NoopTracker();

let activeTracker: ErrorTracker = noopTracker;
let rejectionHandler: ((event: PromiseRejectionEvent) => void) | null = null;

/** Initialize error tracking. Call once at app startup, before React renders. */
export function initErrorTracking(): void {
  const config = getErrorTrackerConfig();

  // Clean up previous listener (prevents duplicates on HMR)
  if (rejectionHandler) {
    window.removeEventListener('unhandledrejection', rejectionHandler);
    rejectionHandler = null;
  }

  if (config.enabled && config.dsn) {
    // Dynamically import SentryTracker only when tracking is enabled.
    // This prevents @sentry/capacitor from loading in dev/preview and causing blank screens.
    import('./SentryTracker').then(({ SentryTracker }) => {
      const tracker = new SentryTracker();
      const result = tracker.init(config);

      const finalize = () => {
        if (tracker.isInitialized()) {
          activeTracker = tracker;

          activeTracker.setTags({
            'app.platform': config.enableNative ? 'native' : 'web',
            'app.release': config.release,
          });

          rejectionHandler = (event) => {
            activeTracker.captureException(event.reason, {
              type: 'unhandledrejection',
            });
          };
          window.addEventListener('unhandledrejection', rejectionHandler);

          console.log(`[ErrorTracking] Initialized for ${config.environment}`);
        } else {
          activeTracker = noopTracker;
          console.log('[ErrorTracking] Init failed, falling back to noop');
        }
      };

      // Handle both sync and async init
      if (result instanceof Promise) {
        result.then(finalize).catch(() => {
          activeTracker = noopTracker;
          console.log('[ErrorTracking] Init failed (async), falling back to noop');
        });
      } else {
        finalize();
      }
    }).catch((err) => {
      console.warn('[ErrorTracking] Failed to load SentryTracker module:', err);
      activeTracker = noopTracker;
    });
  } else {
    activeTracker = noopTracker;
    console.log('[ErrorTracking] Disabled for this environment');
  }
}

/** Capture an exception with optional context. */
export function captureException(error: unknown, context?: Record<string, unknown>): void {
  activeTracker.captureException(error, context);
}

/** Set user context for error tracking. Call on sign-in/out. */
export function setErrorTrackingUser(user: ErrorTrackerUser | null): void {
  activeTracker.setUser(user);
}

/** Set global tags on all future events. */
export function setErrorTrackingTags(tags: Record<string, string | boolean>): void {
  activeTracker.setTags(tags);
}

/** Add a breadcrumb for debugging context. */
export function addErrorTrackingBreadcrumb(breadcrumb: ErrorTrackerBreadcrumb): void {
  activeTracker.addBreadcrumb(breadcrumb);
}
